import {Order, OrderStatusId} from "@/constants/constants";
import {capitalizeFirstLetter} from "@/utils/utils";

const ORDER_TYPE_BY_CODE = Object.freeze({
  [Order.numericType.DELIVERY]: Order.type.DELIVERY,
  [Order.numericType.TAKEAWAY]: Order.type.TAKEAWAY,
  [Order.numericType.DINE_IN]: Order.type.DINE_IN,
});

const ORDER_TYPE_BY_NAME = Object.freeze({
  [normalizeOrderTypeName(Order.type.DELIVERY)]: Order.type.DELIVERY,
  [normalizeOrderTypeName(Order.type.TAKEAWAY)]: Order.type.TAKEAWAY,
  [normalizeOrderTypeName(Order.type.DINE_IN)]: Order.type.DINE_IN,
});

const ORDER_STATUS_KEY_BY_ID = Object.freeze(
  Object.entries(OrderStatusId).reduce((statusKeys, [key, value]) => {
    statusKeys[value] = key;
    return statusKeys;
  }, {})
);

function normalizeOrderTypeName(orderType) {
  return String(orderType || '').trim().toLowerCase().replace(/[\s_-]/g, '');
}

export function getCanonicalOrderType(orderType) {
  return ORDER_TYPE_BY_NAME[normalizeOrderTypeName(orderType)] || null;
}

export function isOrderOrderingEnabled() {
  return window.config?.ORDER?.withOrdering !== false;
}

export function getOrderTypeByCode(orderTypeCode) {
  return ORDER_TYPE_BY_CODE[Number(orderTypeCode)] || null;
}

export function getOrderTypeLabelTranslationKey(orderType) {
  return getOrderTypeLabelTranslationKeys(orderType)[0] || null;
}

export function getOrderTypeLabelTranslationKeys(orderType) {
  const canonicalOrderType = getCanonicalOrderType(orderType);
  return canonicalOrderType ? [
    `orderParameters.label.orderTypeOptions.${canonicalOrderType}`,
    `orderTypeOptions.${canonicalOrderType}`,
  ] : [];
}

export function getOrderTypeLabelTranslationKeyByCode(orderTypeCode) {
  return getOrderTypeLabelTranslationKeysByCode(orderTypeCode)[0] || null;
}

export function getOrderTypeLabelTranslationKeysByCode(orderTypeCode) {
  const canonicalOrderType = getOrderTypeByCode(orderTypeCode);
  return canonicalOrderType ? [
    `orderParameters.label.orderTypeOptions.${canonicalOrderType}`,
    `orderTypeOptions.${canonicalOrderType}`,
  ] : [];
}

function findMatchingTranslationKey(translationKeys, hasTranslation) {
  if (typeof hasTranslation !== 'function') {
    return null;
  }

  return translationKeys.find((translationKey) => hasTranslation(translationKey)) || null;
}

export function getOrderTypeLabel(orderType, translate, hasTranslation) {
  const translationKey = findMatchingTranslationKey(getOrderTypeLabelTranslationKeys(orderType), hasTranslation);
  if (translationKey && typeof translate === 'function') {
    return translate(translationKey);
  }

  return capitalizeFirstLetter(orderType);
}

export function getOrderStatusLabel(statusId, fallbackStatus, translate, hasTranslation) {
  const statusKey = ORDER_STATUS_KEY_BY_ID[Number(statusId)];
  const translationKey = statusKey ? `orderStatus.${statusKey}` : null;

  if (translationKey && typeof hasTranslation === 'function' && hasTranslation(translationKey) && typeof translate === 'function') {
    return translate(translationKey);
  }

  return fallbackStatus;
}

export function getOrderTypeOptionLabel(orderTypeOption, translate, hasTranslation) {
  const translationKeyByCode = findMatchingTranslationKey(getOrderTypeLabelTranslationKeysByCode(orderTypeOption?.code), hasTranslation);
  if (translationKeyByCode && typeof translate === 'function') {
    return translate(translationKeyByCode);
  }

  const translationKeyByName = findMatchingTranslationKey(getOrderTypeLabelTranslationKeys(orderTypeOption?.name), hasTranslation);
  if (translationKeyByName && typeof translate === 'function') {
    return translate(translationKeyByName);
  }

  return capitalizeFirstLetter(orderTypeOption?.name || String(orderTypeOption?.code || ''));
}

export function getShortOrderType(orderType) {
  const canonicalOrderType = getCanonicalOrderType(orderType);
  const typeMap = {
    [Order.type.DELIVERY]: Order.shortType.DELIVERY,
    [Order.type.TAKEAWAY]: Order.shortType.TAKEAWAY,
    [Order.type.DINE_IN]: Order.shortType.DINE_IN,
  };
  return typeMap[canonicalOrderType] || Order.shortType.DINE_IN;
}

export function getNumericOrderType(orderType) {
  const canonicalOrderType = getCanonicalOrderType(orderType);
  const typeMap = {
    [Order.type.DELIVERY]: Order.numericType.DELIVERY,
    [Order.type.TAKEAWAY]: Order.numericType.TAKEAWAY,
    [Order.type.DINE_IN]: Order.numericType.DINE_IN,
  };
  return typeMap[canonicalOrderType] || Order.numericType.DINE_IN;
}

export function getOrderTypeByNumber(numericOrderType) {
  return getOrderTypeByCode(numericOrderType) || Order.type.DINE_IN;
}

/**
 * Cancel an order by guid and shopStore.
 * @param {Object} shopStore - The shop store instance (must have wondId).
 * @param {string} guid - The order guid.
 * @returns {Promise<{exception?: any}>}
 */
export async function cancelOrder(shopStore, guid) {
  const { orderService } = await import("@/services/api/orderService");
  if (!shopStore || !shopStore.wondId || !guid) {
    return { exception: { message: "Missing shopStore.wondId or guid" } };
  }
  try {
    return await orderService.cancelOrder(shopStore.wondId, guid, false);
  } catch (error) {
    return { exception: error };
  }
}
